import { X } from "lucide-react";
import React from "react";
import { Image } from "../../data-display/Image/Image";
import { Dialog, DialogContent, DialogTitle } from "../../primitives/Dialog";
import { ImageOption } from "./ImageOptionPicker.types";

export interface ImageOptionPreviewDialogProps {
  /** The option being previewed, or null when the dialog is closed. */
  option: ImageOption | null;
  onClose: () => void;
}

/** Full-size preview of a single option's image, with its label underneath. */
export const ImageOptionPreviewDialog: React.FC<
  ImageOptionPreviewDialogProps
> = ({ option, onClose }) => (
  <Dialog
    open={option !== null}
    onOpenChange={(isOpen) => {
      if (!isOpen) {
        onClose();
      }
    }}
  >
    <DialogContent
      className="max-w-3xl overflow-hidden p-0"
      // The default close button sits where the picture starts and, stacked
      // over a host modal's own close, reads as a doubled control. This one is
      // pinned to the image and shaped to stay legible on any photo.
      customCloseButton={
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white text-neutral-700 shadow-md transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      }
    >
      {option ? (
        <>
          {option.imageUrl ? (
            <Image
              src={option.imageUrl}
              alt={option.label}
              wrapperClassName="max-h-[70vh] w-full bg-neutral-50"
              className="max-h-[70vh] object-contain"
            />
          ) : null}
          <div className="bg-white px-6 pt-4 pb-6">
            <DialogTitle className="font-semibold text-lg text-neutral-900">
              {option.label}
            </DialogTitle>
            {option.description ? (
              <p className="mt-1 text-content text-sm">{option.description}</p>
            ) : null}
          </div>
        </>
      ) : null}
    </DialogContent>
  </Dialog>
);
